import styled from "styled-components";
import { Sidebar } from "../sidebar/sidebar";
import { SidebarOption } from "../sidebar/SidebarOption";
import VerticalFoldingMenu from "../sidebar/VerticalFoldingMenu";
import { useContext, useEffect, useState } from "react";
import { CurrentUser } from "../../types/User";
import { AuthContext } from "../auth/AuthContext";
import { Roles } from "../../constant/Roles";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { StyledCardStack } from "../../@ui/StyledCardStack";
import LayerBreadcrumbs from "../admin/Layers/LayerNavigation/LayerBreadcrumbs";
import { LayerAdminServiceContext } from "../admin/Layers/LayerAdminServiceContext";
import { LayerInfoOverview } from "../../types/LayerAdmin/LayerInfoOverview";
import { useLayerAdminService } from "../../hooks";

const Content = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
`;

const CardThemeWrapper = styled.div`
  min-height: 0;
`;

export const Admin = () => {
  const authContext = useContext(AuthContext);
  const [user] = useState<CurrentUser | undefined>(authContext?.user);
  const [overviews, setOverviews] = useState<LayerInfoOverview[]>();
  const navigate = useNavigate();
  const location = useLocation();
  const layerAdminService = useLayerAdminService();

  useEffect(() => {
    layerAdminService
      .getOverview()
      .then((x) => {
        setOverviews(x);
      })
      .catch(() => {});
  }, [layerAdminService]);

  useEffect(() => {
    if (location.pathname === "/admin") {
      navigate("/admin/orders");
    }
  }, [location.pathname, navigate]);

  const sidebarMenuNavigate = (url: string) => {
    navigate(url as string);
  };

  const getSidebarOptions = (user: CurrentUser): SidebarOption[] => {
    const sidebarOptions: SidebarOption[] = [
      {
        label: "Orders",
        url: "/admin/orders",
      },
      {
        label: "Royalties",
        url: "/admin/royalties",
      },
      {
        label: "Users",
        url: "/admin/users",
      },
      {
        label: "Queue",
        url: "/admin/queue",
      },
      {
        label: "Processing Times",
        url: "/admin/processingtimes",
      },
      {
        label: "Licences",
        url: "/admin/licences",
      },
      {
        label: "WFS Requests",
        children: [
          {
            label: "History",
            url: "/admin/request-history",
          },
          {
            label: "Response Times",
            url: "/admin/request-statistics",
          },
        ],
      },
      {
        label: "Layers",
        url: "/admin/layers",
        children: overviews
          ?.filter((x) => x.allowImport)
          .map((x) => ({
            label: x.layerName,
            url: `/admin/layers/${x.layerName}`,
          })),
      },
      {
        label: "Banner / Maintenance",
        url: "/admin/banner",
      },
      {
        label: "Notifications",
        url: "/admin/notifications",
      },
      {
        label: "User Order Data",
        url: "/admin/user-order-data",
      },
    ];

    return sidebarOptions;
  };

  const sidebarOptions: SidebarOption[] = getSidebarOptions(
    user as CurrentUser,
  );

  return (
    <Content>
      <Sidebar>
        <VerticalFoldingMenu
          url={location.pathname}
          options={sidebarOptions}
          onSelect={sidebarMenuNavigate}
        />
      </Sidebar>
      <CardThemeWrapper className="card-theme w-100 d-flex">
        <StyledCardStack className="flex-column d-flex rounded bg-white m-4 pb-2 pt-4 px-4 flex-grow w-100">
          {location.pathname.includes("/admin/layers") && <LayerBreadcrumbs />}

          <LayerAdminServiceContext.Provider
            value={{
              overviews,
              layerAdminService,
            }}
          >
            <Outlet />
          </LayerAdminServiceContext.Provider>
        </StyledCardStack>
      </CardThemeWrapper>
    </Content>
  );
};
